import { useMemo } from "react";

import { DropDownElement } from "src/base-components/DropDown";
import { FixedSimpleDropdown } from "src/base-components/FixedSimpleDropdown";
import { PropertyDefinitionOutputTypeEnum } from "src/clients/features-control";
import { TypeIcon } from "src/entities/entityView/EntityDetailsSidePane";
import {
  FILTERABLE_TYPES,
  JUNCTION_OPTIONS,
} from "src/eventsCatalogue/filters/constants";
import { getEventProperties } from "src/eventsCatalogue/filters/utils";
import { useEventSchemas } from "src/eventsCatalogue/queries";
import { findEventSchema } from "src/eventsCatalogue/utils";

type EventFieldsDropdownProps = {
  dataLoc: string;
  eventType: string;
  selectedKey: string;
  type: "field" | "junction";
  disabled?: boolean;
  onSelect: (key: string) => void;
};

export const EventFieldsDropdown: React.FC<EventFieldsDropdownProps> = ({
  dataLoc,
  eventType,
  selectedKey,
  type,
  disabled,
  onSelect,
}) => {
  const { data: eventSchemas } = useEventSchemas();
  const currentEvent = findEventSchema(eventType, eventSchemas);

  const eventProperties = useMemo(
    () => getEventProperties(currentEvent),
    [currentEvent],
  );

  const renderValue = (displayName: string, icon: React.ReactNode) => (
    <div className="flex w-full items-center gap-x-1 overflow-hidden">
      {icon}
      <span className="truncate">{displayName}</span>
    </div>
  );

  const fieldOptions = useMemo(() => {
    return eventProperties
      .filter((property) => {
        const datatype = property.datatype as PropertyDefinitionOutputTypeEnum;
        const isFilterableType = FILTERABLE_TYPES.includes(datatype);

        // Exclude timestamp fields since we already have a date range picker
        const isTimestampField = property.name
          .toLowerCase()
          .includes("timestamp");

        return isFilterableType && !isTimestampField;
      })
      .map((property) => {
        const propertyType = property.datatype;
        return {
          key: property.name,
          value: renderValue(
            property.displayName,
            <TypeIcon cardinality={undefined} type={propertyType} />,
          ),
        };
      });
  }, [eventProperties]);

  const elements = type === "field" ? fieldOptions : JUNCTION_OPTIONS;
  const placeholder = type === "field" ? "Select field" : "and";

  return (
    <FixedSimpleDropdown
      dataLoc={dataLoc}
      disabled={disabled}
      elements={elements as DropDownElement<string>[]}
      placeholder={placeholder}
      selectedKey={selectedKey}
      onSelect={onSelect}
    />
  );
};
